import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/api";
import { toast } from "sonner";
import { MessageCircle, Download } from "lucide-react";
import { openWhatsApp, waNumber } from "@/lib/whatsapp";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{children}</Label>
);

/**
 * Reusable click-to-chat sender: pick a customer from Customer Master, edit the
 * message, then open their WhatsApp chat. If a file is given it is shared with the
 * native share sheet on mobile, or saved for attaching on desktop.
 */
export const WhatsAppSendDialog = ({
  open,
  onOpenChange,
  title = "Send on WhatsApp",
  defaultCustomer = "",
  message = "",
  file = null,
  fileLabel = "attachment",
  contactsPath = "/customers/contacts",
  contactLabel = "Customer",
}) => {
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState(defaultCustomer);
  const [phone, setPhone] = useState("");
  const [text, setText] = useState(message);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setText(message);
    setName(defaultCustomer);
    api
      .get(contactsPath)
      .then(({ data }) => {
        setContacts(data);
        const hit = data.find((c) => c.name === defaultCustomer);
        setPhone(hit ? hit.whatsapp || hit.phone || "" : "");
      })
      .catch(() => {});
  }, [open, defaultCustomer, message, contactsPath]);

  const pick = (v) => {
    setName(v);
    const hit = contacts.find((c) => c.name === v);
    setPhone(hit ? hit.whatsapp || hit.phone || "" : "");
  };

  const saveFile = () => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const send = async () => {
    if (!waNumber(phone)) return toast.error("Enter a WhatsApp number for this customer");
    setSending(true);
    try {
      if (file && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else {
        if (file) {
          saveFile();
          toast.info(`${fileLabel} saved — attach it in the WhatsApp chat that just opened`);
        }
        openWhatsApp(phone, text);
      }
      onOpenChange(false);
    } catch {
      /* user dismissed the share sheet */
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="whatsapp-send-dialog" className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-head text-xl font-extrabold tracking-tighter">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            Opens the {contactLabel.toLowerCase()}'s WhatsApp chat with this message ready to send.
          </DialogDescription>
        </DialogHeader>

        <div>
          <L>{contactLabel}</L>
          <select
            data-testid="whatsapp-customer-select"
            value={name}
            onChange={(e) => pick(e.target.value)}
            className="mt-2 h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">Select {contactLabel.toLowerCase()}</option>
            {contacts.map((c) => (
              <option key={c.name} value={c.name}>
                {`${c.name}${c.whatsapp || c.phone ? ` · ${c.whatsapp || c.phone}` : " · no number"}`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <L>WhatsApp number</L>
          <Input
            data-testid="whatsapp-number-input"
            className="mt-2 rounded-sm"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="10 digit mobile"
          />
          <p className="mt-1 text-xs text-zinc-500">
            Saved numbers come from Admin → {contactLabel} Master → {contactLabel} details.
          </p>
        </div>

        <div>
          <L>Message</L>
          <Textarea
            data-testid="whatsapp-message-input"
            rows={5}
            className="mt-2 rounded-sm"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            data-testid="whatsapp-send-button"
            disabled={sending}
            className="flex-1 rounded-sm bg-[#128C7E] text-white hover:bg-[#0f7568]"
            onClick={send}
          >
            <MessageCircle size={15} className="mr-1.5" /> Open WhatsApp chat
          </Button>
          {file && (
            <Button
              data-testid="whatsapp-save-file-button"
              variant="outline"
              className="rounded-sm"
              onClick={saveFile}
            >
              <Download size={15} className="mr-1.5" /> Save {fileLabel}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
